import debounce from "lodash/debounce";
import { ApiResult, FieldType, ViewModelBase } from "@shoothill/core";
import { observable, action, computed, runInAction } from "mobx";

import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { ApprovalSignalRModel, ApprovalSignalRModelDTO } from "./ApprovalSignalRModel";
import { SignalRService } from "Services/SignalRService";
import { ApprovalListViewModel } from "../ApprovalListViewModel";
import { PurchaseListViewModel } from "../PurchaseListViewModel";

export class ApprovalSignalRViewModel extends ViewModelBase<ApprovalSignalRModel> {
    public server: ServerViewModel = new ServerViewModel();

    // #region Constants

    // #endregion Constants

    private static _instance: ApprovalSignalRViewModel;
    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    public signalRService: SignalRService | undefined;

    public initializeSignalR = () => {
        this.signalRService = SignalRService.getInstance(AppUrls.Server.Hubs.ApprovalHub);

        if (this.signalRService && this.signalRService.hubConnection) {
            // Register the shared debounced function
            this.signalRService.hubConnection.on("ApprovalUpdated", this.debouncedApprovalUpdatedHandler);
        }
    };

    public debouncedApprovalUpdatedHandler = debounce((affectedPages: string[]) => {
        // Call the top-level action (always active)
        ApprovalSignalRViewModel.Instance.getAllApprovalCounts();

        // Call the page-specific action (only if the page is active)
        if (ApprovalListViewModel.Instance.getIsPageActive === true) {
            if (affectedPages.some((i) => i === ApprovalListViewModel.Instance.tabsViewModel.activeTabItemViewModel?.id)) {
                switch (ApprovalListViewModel.Instance.tabsViewModel.activeTabItemViewModel?.id) {
                    case ApprovalListViewModel.TAB_REQUIRED_APPROVALS:
                        ApprovalListViewModel.Instance.getMyApprovalsNew(true);
                        break;
                    case ApprovalListViewModel.TAB_ALL_APPROVALS:
                        ApprovalListViewModel.Instance.getMyApprovalsNew(false);
                        break;
                    case ApprovalListViewModel.TAB_RESOLVED:
                        ApprovalListViewModel.Instance.getMyApprovalsActioned();
                        break;
                    default:
                        break;
                }
            }
        } else if (PurchaseListViewModel.Instance.getIsPageActive === true) {
            if (affectedPages.some((i) => i === PurchaseListViewModel.Instance.tabsViewModel.activeTabItemViewModel?.id)) {
                const statusName = PurchaseListViewModel.Instance.getRequisitionPOStatusNameByType(Number(PurchaseListViewModel.Instance.tabsViewModel.activeTabItemViewModel?.id));
                PurchaseListViewModel.Instance.getMyAmends(statusName);
            }
        }
    }, 5000); // 5000ms delay

    public constructor() {
        super(new ApprovalSignalRModel(), false);

        this.setDecorators(ApprovalSignalRModel);
        this.initializeSignalR();
    }

    /**
     * Load dependent data from the API.
     */
    public initialize = () => {
        if (!this.hasLoaded) {
            this.getAllApprovalCounts();
        }
    };

    @observable
    public errorMessage: string = "";

    @observable
    public hasLoaded = false;

    @action
    private setHasLoaded(val: boolean) {
        this.hasLoaded = val;
    }

    @computed
    public get getTotalCount() {
        return this.model.totalCount;
    }

    @computed
    public get getRequiredCount() {
        return this.model.required;
    }

    @computed
    public get getAmendCount() {
        return this.model.amendRequired;
    }

    @action
    public getAllApprovalCounts = async (): Promise<void> => {
        this.setIsLoading(true);
        const apiResult: ApiResult<ApprovalSignalRModelDTO> = await this.Get(AppUrls.Server.Approval.GetAllApprovalCounts);

        this.setHasLoaded(false);
        if (apiResult.wasSuccessful) {
            runInAction(() => {
                this.model.fromDto(apiResult.payload);
            });
        } else {
            console.log(apiResult.errors);
        }

        this.setHasLoaded(true);
        this.setIsLoading(false);
    };

    @action
    public cleanUp = () => {
        // TODO Any Cleanup Code here. e.g. if  a user or project or client etc, wipe it from the instance on page shutdown
    };

    // #region Approval Modals

    // #endregion Approval Modals

    public async isFieldValid(fieldName: keyof FieldType<ApprovalSignalRModel>): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
